import React, { useState, useEffect } from 'react';
import { connect, useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { withStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import styles from '../styles';
import Header from '~/pages/Manager/Header';
import Footer from '~/pages/Manager/Footer';
import PF2MTable from '~/components/PF2MTable';
import PF2MAlertDialog from '~/components/PF2MAlertDialog';

import {
  getElementsTemplate,
  exportElements,
  importElements,
  getElements,
  setElementValue,
  updateElements,
  resetElements,
  resetFileUploadErrors,
} from '~/store/manager/actions';
import ElementsFilter from './ElementsFilter';

const ElementsTab = ({
  classes,
  elements,
  getElementsTemplate: getTemplateAction,
  exportElements: exportAction,
  importElements: importAction,
  getElements: getAll,
  isElementsDirty,
  updateElements: update,
  resetElements: reset,
  setElementValue: updateItem,
}) => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const [dataLoaded, setDataLoaded] = useState(true);
  const [showDialogImport, setShowDialogImport] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [currentFilter, setCurrentFilter] = useState({});
  const [disableHeader, setDisableHeader] = useState(true);
  const fileUploadErrors = useSelector(state => state.manager.fileUploadErrors);

  useEffect(() => {
    async function fetchData() {
      await getAll(false, currentFilter);
      setDisableHeader(false);
    }

    if (!dataLoaded) {
      fetchData();
      setDataLoaded(true);
    }
  }, [dataLoaded, getAll, currentFilter, setDisableHeader]);

  useEffect(() => {
    if (Object.keys(currentFilter).length > 0) {
      setDataLoaded(false);
    }
  }, [currentFilter]);

  useEffect(() => {
    if (fileUploadErrors && fileUploadErrors.length > 0) {
      const message = fileUploadErrors.map((error) => {
        const [rowCol, description] = error.split('-');
        const [row, col] = rowCol.split(';');
        return `${translate('validation:Row')} ${row} ${translate('validation:Col')} ${col} - ${translate(`validation:${description}`)}`;
      });
      setDialogMessage(message.join('\r\n'));
      setShowDialogImport(true);
    }
  }, [fileUploadErrors]);

  const columns = [
    {
      field: 'name',
      title: translate('common:Name'),
      align: 'center',
      editable: true,
      updateOnChange: true,
    },
    {
      field: 'active', title: 'STATUS', editable: true,
    },
    {
      field: 'polygon', title: translate('common:Polygon'), width: '100%', truncate: true,
    },
  ];

  return (
    <div className={classes.tabContainer}>
      <ElementsFilter setCurrentFilter={setCurrentFilter} />
      <Header
        getTemplateAction={getTemplateAction}
        importAction={importAction}
        exportAction={exportAction}
        currentFilter={currentFilter}
        disabled={disableHeader}
      />
      <PF2MTable
        customStyles={{ height: 'calc(100vh - 52px - 52px - 180px - 103px)' }}
        data={elements}
        columns={columns}
        updateItem={updateItem}
      />
      <Footer
        isDirty={isElementsDirty}
        discard={() => reset()}
        sendData={() => update(elements)}
        style={{ marginTop: 10 }}
      />
      <PF2MAlertDialog
        title={translate('validation:ImportErrors')}
        description={dialogMessage}
        open={showDialogImport}
        onConfirm={() => {
          setShowDialogImport(false);
          dispatch(resetFileUploadErrors());
        }}
        onClose={() => setShowDialogImport(false)}
        hasCancel={false}
      />
    </div>
  );
};

ElementsTab.propTypes = {
  classes: PropTypes.object.isRequired,
  elements: PropTypes.array,
  getElementsTemplate: PropTypes.func,
  exportElements: PropTypes.func,
  importElements: PropTypes.func,
  getElements: PropTypes.func,
  isElementsDirty: PropTypes.bool,
  updateElements: PropTypes.func,
  resetElements: PropTypes.func,
  setElementValue: PropTypes.func,
};

ElementsTab.defaultProps = {
  elements: [],
  getElementsTemplate: () => {},
  exportElements: () => {},
  importElements: () => {},
  getElements: () => {},
  isElementsDirty: false,
  updateElements: null,
  resetElements: null,
  setElementValue: null,
};

const mapStateToProps = state => ({
  elements: state.manager.elements,
  isElementsDirty: state.manager.isElementsDirty,
});

const mapDispatchToProps = {
  getElementsTemplate,
  exportElements,
  importElements,
  getElements,
  updateElements,
  resetElements,
  setElementValue,
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(ElementsTab));
